import { useState } from 'react'
import { X, ChevronDown, ChevronRight } from 'lucide-react'
import type { ResponseStatus } from '../types'

interface QuestionStatus {
  questionNumber: number
  status: ResponseStatus
  isMarkedForReview: boolean
  hasAnswer: boolean
  sectionId: string
  sectionName: string
}

interface Section {
  sectionId: string
  sectionName: string
  questions: Array<QuestionStatus & { index: number }>
}

interface QuestionPaletteProps {
  questions: QuestionStatus[]
  currentIndex: number
  onSelectQuestion: (index: number) => void
  onClose?: () => void
  showCloseButton?: boolean
}

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  answered: { bg: 'bg-green-500', text: 'text-white', border: 'border-green-500' },
  answeredReview: { bg: 'bg-purple-500', text: 'text-white', border: 'border-purple-500' },
  notAnswered: { bg: 'bg-red-500', text: 'text-white', border: 'border-red-500' },
  notAnsweredReview: { bg: 'bg-purple-300', text: 'text-purple-900', border: 'border-purple-300' },
  notVisited: { bg: 'bg-gray-200', text: 'text-gray-600', border: 'border-gray-300' },
  current: { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-600' },
}

function getQuestionStatusKey(q: QuestionStatus, isCurrent: boolean): string {
  if (isCurrent) return 'current'
  if (q.status === 'UNSEEN') return 'notVisited'
  if (q.hasAnswer && q.isMarkedForReview) return 'answeredReview'
  if (q.hasAnswer) return 'answered'
  if (q.isMarkedForReview) return 'notAnsweredReview'
  return 'notAnswered'
}

export default function QuestionPalette({
  questions,
  currentIndex,
  onSelectQuestion,
  onClose,
  showCloseButton = false,
}: QuestionPaletteProps) {
  // Group questions by section
  const sections: Section[] = []
  const sectionMap = new Map<string, Section>()

  questions.forEach((q, index) => {
    let section = sectionMap.get(q.sectionId)
    if (!section) {
      section = {
        sectionId: q.sectionId,
        sectionName: q.sectionName,
        questions: [],
      }
      sectionMap.set(q.sectionId, section)
      sections.push(section)
    }
    section.questions.push({ ...q, index })
  })

  // Track expanded sections (all expanded by default)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(sections.map(s => s.sectionId))
  )

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  // Count by status
  const counts = {
    answered: questions.filter(q => q.hasAnswer && !q.isMarkedForReview).length,
    answeredReview: questions.filter(q => q.hasAnswer && q.isMarkedForReview).length,
    notAnswered: questions.filter(q => !q.hasAnswer && q.status !== 'UNSEEN' && !q.isMarkedForReview).length,
    notAnsweredReview: questions.filter(q => !q.hasAnswer && q.isMarkedForReview).length,
    notVisited: questions.filter(q => q.status === 'UNSEEN').length,
  }

  // Get section stats
  const getSectionStats = (sectionQuestions: Array<QuestionStatus & { index: number }>) => {
    const answered = sectionQuestions.filter(q => q.hasAnswer).length
    return { answered, total: sectionQuestions.length }
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Questions</h3>
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-3 border-b border-gray-200 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded ${STATUS_COLORS.answered.bg}`}></span>
            <span className="text-gray-600">Answered ({counts.answered})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded ${STATUS_COLORS.notAnswered.bg}`}></span>
            <span className="text-gray-600">Not Answered ({counts.notAnswered})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded ${STATUS_COLORS.notVisited.bg}`}></span>
            <span className="text-gray-600">Not Visited ({counts.notVisited})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded ${STATUS_COLORS.answeredReview.bg}`}></span>
            <span className="text-gray-600">Review ({counts.answeredReview + counts.notAnsweredReview})</span>
          </div>
        </div>
      </div>

      {/* Sections with Questions */}
      <div className="flex-1 overflow-y-auto">
        {sections.map((section) => {
          const isExpanded = expandedSections.has(section.sectionId)
          const stats = getSectionStats(section.questions)

          return (
            <div key={section.sectionId} className="border-b border-gray-100 last:border-b-0">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.sectionId)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                  <span className="font-medium text-gray-800 text-sm">{section.sectionName}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {stats.answered}/{stats.total}
                </span>
              </button>

              {/* Section Questions Grid */}
              {isExpanded && (
                <div className="p-3">
                  <div className="grid grid-cols-5 gap-2">
                    {section.questions.map((q) => {
                      const statusKey = getQuestionStatusKey(q, q.index === currentIndex)
                      const colors = STATUS_COLORS[statusKey]

                      return (
                        <button
                          key={q.index}
                          onClick={() => onSelectQuestion(q.index)}
                          className={`w-full aspect-square rounded-lg text-sm font-medium transition-all ${colors.bg} ${colors.text} border-2 ${colors.border} ${
                            q.index === currentIndex ? 'ring-2 ring-blue-300 ring-offset-1' : ''
                          } hover:opacity-90`}
                        >
                          {q.questionNumber}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
